import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../../modules/mat-angular.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { MatTooltipModule } from '@angular/material/tooltip';

// Reusable components
import { BarChartComponent } from '../../../charts/bar-chart/bar-chart.component';
import { PieGridComponent } from '../../../charts/pie-grid/pie-grid.component';
import { PolarChartComponent } from '../../../charts/polar-chart/polar-chart.component';
import { LineChartComponent } from '../../../charts/line-chart/line-chart.component';
import { TableComponent, TableColumn } from '../../table/table.component';
import { ScoreCardsComponent } from '../../score-cards/score-cards.component';
import { PieComponent } from '../../pie/pie.component';

@Component({
  selector: 'app-common-performance-tracking',
  imports: [
    CommonModule,
    MatAngularModule,
    NgxChartsModule,
    MatTooltipModule,
    BarChartComponent,
    PieGridComponent,
    PolarChartComponent,
    LineChartComponent,
    TableComponent,
    ScoreCardsComponent,
    PieComponent,
  ],
  templateUrl: './common-performance-tracking.component.html',
  styleUrl: './common-performance-tracking.component.scss'
})
export class CommonPerformanceTrackingComponent implements OnInit, OnChanges {
  @Input() data: any = null;
  @Input() universities: string[] = [];
  @Input() title: string = 'Performance Tracking';

  // Chart data for different visualizations
  performanceRadarData: any[] = [];
  trendLineData: any[] = [];
  improvementBarData: any[] = [];
  performanceBarData: any[] = [];
  performancePieData: any[] = [];

  // Table data
  performanceTableData: any[] = [];
  trendTableData: any[] = [];
  improvementTableData: any[] = [];

  // Table configurations
  performanceTableColumns: TableColumn[] = [
    { key: 'university', label: 'University', type: 'text' },
    { key: 'academic_performance', label: 'Academic Performance', type: 'percentage' },
    { key: 'research_output', label: 'Student Success', type: 'percentage' },
    { key: 'student_satisfaction', label: 'Financial Performance', type: 'percentage' },
    { key: 'employment_rate', label: 'International Performance', type: 'percentage' },
    { key: 'international_recognition', label: 'Innovation Performance', type: 'percentage' }
  ];

  trendTableColumns: TableColumn[] = [
    { key: 'university', label: 'University', type: 'text' },
    { key: 'current_score', label: 'Current Score', type: 'percentage' },
    { key: 'previous_score', label: 'Previous Score', type: 'percentage' },
    { key: 'improvement', label: 'Improvement', type: 'percentage' },
    { key: 'trend', label: 'Trend', type: 'text' }
  ];

  improvementTableColumns: TableColumn[] = [
    { key: 'university', label: 'University', type: 'text' },
    { key: 'metric', label: 'Metric', type: 'text' },
    { key: 'current_value', label: 'Current Value', type: 'percentage' },
    { key: 'target_value', label: 'Target Value', type: 'percentage' },
    { key: 'gap', label: 'Gap', type: 'percentage' }
  ];

  // Performance metrics
  performanceMetrics: any[] = [];
  overallScores: any[] = [];

  // Enhanced API response data
  overallPerformanceSummary: any = null;
  trackingMethodology: any = null;
  details: any = null;
  metadata: any = null;

  // Evidence sources and recommendations
  evidenceSources: any[] = [];
  recommendations: any[] = [];

  ngOnInit(): void {
    this.processData();

    // Ensure we have some data for the pie charts even if no real data is provided
    if (this.performanceBarData.length === 0) {
      this.performanceBarData = [
        {
          name: 'University of Hull',
          series: [
            { name: 'Academic Performance', value: 78 },
            { name: 'Student Success', value: 85 },
            { name: 'Financial Performance', value: 72 },
            { name: 'International Performance', value: 80 },
            { name: 'Innovation Performance', value: 75 }
          ]
        },
        {
          name: 'Warwick Business School',
          series: [
            { name: 'Academic Performance', value: 95 },
            { name: 'Student Success', value: 90 },
            { name: 'Financial Performance', value: 88 },
            { name: 'International Performance', value: 92 },
            { name: 'Innovation Performance', value: 87 }
          ]
        }
      ];

      // Create performance pie chart data
      this.performancePieData = this.performanceBarData.map(uni => ({
        name: uni.name,
        value: uni.series ? uni.series.reduce((sum: number, s: any) => sum + s.value, 0) / uni.series.length : 0
      }));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.processData();
    }
  }

  private processData(): void {
    if (!this.data) return;

    // Handle both direct data and transformed data from service
    const responseData = this.data.performance_tracking_data || this.data;

    // Extract evidence sources and recommendations
    this.evidenceSources = responseData.evidence_sources || [];
    this.recommendations = responseData.recommendations || [];
    this.details = responseData.details || null;
    this.metadata = responseData.metadata || null;

    // Process universities data from the new API structure
    if (responseData.data && Array.isArray(responseData.data)) {
      this.processUniversitiesData(responseData.data);
    }

    // Process universities data from the universities array
    if (responseData.universities && Array.isArray(responseData.universities)) {
      this.processUniversitiesData(responseData.universities);
    }

    // Process table data from the new API structure
    if (responseData.table && Array.isArray(responseData.table)) {
      this.processTableData(responseData.table);
    }

    // Process chart data from the new API structure
    if (responseData.chart && responseData.chart.series) {
      this.processChartData(responseData.chart.series);
    }

    // Process trend data
    if (responseData.trend_data && Array.isArray(responseData.trend_data)) {
      this.processTrendData(responseData.trend_data);
    }

    // Process overall performance summary
    if (responseData.overall_performance_summary) {
      this.processOverallPerformanceSummary(responseData.overall_performance_summary);
    }

    // Process tracking methodology
    if (responseData.tracking_methodology) {
      this.processTrackingMethodology(responseData.tracking_methodology);
    }

    // Process performance metrics
    this.processPerformanceMetrics();
  }

  private processUniversitiesData(universities: any[]): void {
    // Create performance radar chart data from the series
    this.performanceRadarData = universities.map(uni => ({
      name: uni.name,
      series: uni.series || []
    }));

    // Create performance table data - handle the actual sample data structure
    this.performanceTableData = universities.map(uni => {
      const seriesMap = (uni.series || []).reduce((acc: any, item: any) => {
        acc[item.name.toLowerCase().replace(/\s+/g, '_')] = item.value;
        return acc;
      }, {});

      return {
        university: uni.name,
        academic_performance: seriesMap.academic_performance || 0,
        research_output: seriesMap.student_success || 0, // Map student_success to research_output
        student_satisfaction: seriesMap.financial_performance || 0, // Map financial_performance to student_satisfaction
        employment_rate: seriesMap.international_performance || 0, // Map international_performance to employment_rate
        international_recognition: seriesMap.innovation_performance || 0 // Map innovation_performance to international_recognition
      };
    });

    // Create performance bar chart data (matching influence comparison structure)
    this.performanceBarData = universities.map(uni => ({
      name: uni.name,
      series: uni.series || []
    }));

    // Create performance pie chart data
    this.performancePieData = universities.map(uni => ({
      name: uni.name,
      value: uni.series ? uni.series.reduce((sum: number, s: any) => sum + s.value, 0) / uni.series.length : 0
    }));

    // Calculate overall scores for score cards
    this.overallScores = universities.map(uni => {
      const avgScore = uni.series ?
        uni.series.reduce((sum: number, item: any) => sum + item.value, 0) / uni.series.length : 0;

      return {
        name: uni.name,
        'Overall Performance Score': avgScore, // Use the metric name as the property key
        value: avgScore, // Keep for backward compatibility
        label: 'Overall Performance Score'
      };
    });
  }

  private processTableData(tableData: any[]): void {
    // Process performance data from table - handle the actual sample data structure
    this.performanceTableData = tableData.map(item => ({
      university: item.university,
      academic_performance: item.academic_performance || 0,
      research_output: item.student_success || 0, // Map student_success to research_output
      student_satisfaction: item.financial_performance || 0, // Map financial_performance to student_satisfaction
      employment_rate: item.international_performance || 0, // Map international_performance to employment_rate
      international_recognition: item.innovation_performance || 0 // Map innovation_performance to international_recognition
    }));
  }

  private processChartData(chartData: any[]): void {
    // Process performance chart data (matching influence comparison structure)
    this.performanceBarData = chartData.map(uni => ({
      name: uni.name,
      series: uni.series || []
    }));

    // Create performance pie chart data
    this.performancePieData = chartData.map(uni => ({
      name: uni.name,
      value: uni.series ? uni.series.reduce((sum: number, s: any) => sum + s.value, 0) / uni.series.length : 0
    }));
  }

  private processTrendData(trendData: any[]): void {
    // Create trend line chart data
    this.trendLineData = trendData.map(item => ({
      name: item.university,
      series: item.data_points?.map((point: any) => ({
        name: point.month,
        value: point.score
      })) || []
    }));

    // Create trend table data
    this.trendTableData = trendData.map(item => ({
      university: item.university,
      current_score: item.current_score,
      previous_score: item.previous_score,
      improvement: item.improvement,
      trend: item.trend
    }));

    // Create improvement bar chart data
    this.improvementBarData = trendData.map(item => ({
      name: item.university,
      series: [{
        name: 'Improvement',
        value: item.improvement
      }]
    }));

    // Create improvement table data
    this.improvementTableData = trendData.map(item => ({
      university: item.university,
      metric: 'Overall Performance',
      current_value: item.current_score,
      target_value: Math.min(100, item.current_score + 5), // Target 5 points higher
      gap: Math.min(100, item.current_score + 5) - item.current_score
    }));
  }

  private processOverallPerformanceSummary(summary: any): void {
    this.overallPerformanceSummary = summary;
  }

  private processTrackingMethodology(methodology: any): void {
    this.trackingMethodology = methodology;
  }

  private processPerformanceMetrics(): void {
    // Create performance metrics for pie chart
    if (this.performanceTableData.length > 0) {
      const firstUni = this.performanceTableData[0];
      this.performanceMetrics = [
        { name: 'Academic Performance', value: firstUni.academic_performance },
        { name: 'Student Success', value: firstUni.research_output }, // Map research_output to Student Success
        { name: 'Financial Performance', value: firstUni.student_satisfaction }, // Map student_satisfaction to Financial Performance
        { name: 'International Performance', value: firstUni.employment_rate }, // Map employment_rate to International Performance
        { name: 'Innovation Performance', value: firstUni.international_recognition } // Map international_recognition to Innovation Performance
      ];
    }
  }

  // Helper methods for template
  getPerformanceTableColumns(): string[] {
    return this.performanceTableColumns.map(col => col.key);
  }

  getTrendTableColumns(): string[] {
    return this.trendTableColumns.map(col => col.key);
  }

  getImprovementTableColumns(): string[] {
    return this.improvementTableColumns.map(col => col.key);
  }

  hasPerformanceData(): boolean {
    return this.performanceTableData.length > 0;
  }

  hasTrendData(): boolean {
    return this.trendTableData.length > 0;
  }

  hasImprovementData(): boolean {
    return this.improvementTableData.length > 0;
  }

  hasEvidenceSources(): boolean {
    return this.evidenceSources.length > 0;
  }

  hasRecommendations(): boolean {
    return this.recommendations.length > 0;
  }

  hasOverallPerformanceSummary(): boolean {
    return this.overallPerformanceSummary !== null;
  }

  hasTrackingMethodology(): boolean {
    return this.trackingMethodology !== null;
  }

  hasDetails(): boolean {
    return this.details !== null;
  }

  hasMetadata(): boolean {
    return this.metadata !== null;
  }

  getTrendColor(trend: string): string {
    switch (trend?.toLowerCase()) {
      case 'upward': return 'green';
      case 'downward': return 'red';
      case 'stable': return 'blue';
      default: return 'gray';
    }
  }

  getTrendIcon(trend: string): string {
    switch (trend?.toLowerCase()) {
      case 'upward': return 'trending_up';
      case 'downward': return 'trending_down';
      case 'stable': return 'trending_flat';
      default: return 'trending_flat';
    }
  }

  // Pie chart methods for performance comparison (matching influence comparison structure)
  getPerformanceMetrics(): string[] {
    if (this.performanceBarData.length > 0 && this.performanceBarData[0].series) {
      return this.performanceBarData[0].series.map((s: any) => s.name);
    }
    // Return default metrics for testing
    return ['Academic Performance', 'Student Success', 'Financial Performance', 'International Performance', 'Innovation Performance'];
  }

  getUniversitiesForPieCharts(): any[] {
    return this.performanceBarData.map(item => ({
      name: item.name,
      ...this.extractMetricsFromSeries(item.series)
    }));
  }

  private extractMetricsFromSeries(series: any[]): any {
    const metrics: any = {};
    if (series && Array.isArray(series)) {
      series.forEach(s => {
        if (s.name && s.value !== undefined) {
          metrics[s.name] = s.value;
        }
      });
    }
    return metrics;
  }

  getPieChartData(university: any, metric: string): any[] {
    const score = university[metric] || 0;
    const maxScore = 100; // Assuming max score is 100
    return [
      {
        name: metric,
        value: score
      },
      {
        name: 'Remaining',
        value: maxScore - score
      }
    ];
  }

  onPieChartSelect(event: any): void {
    // // console.log('Pie chart selected:', event);
  }

  getEvidenceTooltip(evidence: any): string {
    if (!evidence) return '';

    let tooltip = '';
    if (evidence.category) tooltip += `Category: ${evidence.category}\n`;
    if (evidence.confidence && typeof evidence.confidence === 'number') {
      tooltip += `Confidence: ${Math.round(evidence.confidence * 100)}%\n`;
    }
    if (evidence.snippet) tooltip += `Snippet: ${evidence.snippet}\n`;
    if (evidence.url) tooltip += `\nClick to view source`;

    return tooltip.trim();
  }

  openSourceLink(url: string): void {
    if (url && url.trim() !== '' && url !== '#') {
      // Open the link in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Show a message if no URL is available
      // console.log('No source URL available for this item');
    }
  }
}
